import { Controller } from "@hotwired/stimulus"

// Composant select avec recherche réutilisable
export default class extends Controller {
  static targets = [
    "input",           // Champ de recherche visible
    "hiddenField",     // Champ caché pour la valeur sélectionnée
    "dropdown",        // Dropdown avec les options
    "options",         // Container des options
    "noResults",       // Message "Aucun résultat"
    "selected",        // Option sélectionnée affichée
    "trigger"          // Élément déclencheur pour l'ouverture
  ]
  
  static values = {
    url: String,           // URL pour la recherche AJAX (optionnel)
    placeholder: String,   // Placeholder du champ de recherche
    selected: String,      // Valeur sélectionnée
    selectedText: String,  // Texte de l'option sélectionnée
    minChars: { type: Number, default: 2 },  // Minimum de caractères pour chercher
    maxResults: { type: Number, default: 10 } // Maximum de résultats
  }
  
  static classes = [
    "open",      // Classe quand le dropdown est ouvert
    "selected",  // Classe pour l'option sélectionnée
    "highlight"  // Classe pour l'option en surbrillance (navigation clavier)
  ]

  connect() {
    console.log('🎯 SearchableSelect CONNECTED!', this.element)
    
    this.isOpen = false
    this.highlightedIndex = -1
    this.allOptions = []
    this.filteredOptions = []
    this.searchTimeout = null
    
    // Stocker toutes les options initiales
    this.storeInitialOptions()
    
    // Afficher la valeur sélectionnée si elle existe
    if (this.selectedValue && this.selectedTextValue) {
      this.displaySelectedValue()
    }
    
    // Fermer le dropdown si on clique à l'extérieur
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    document.addEventListener('click', this.boundCloseOnClickOutside)
    
    // Debug - vérifier tous les targets
    console.log('SearchableSelect connected - Targets check:', {
      element: this.element,
      hasDropdownTarget: this.hasDropdownTarget,
      hasInputTarget: this.hasInputTarget,
      hasHiddenFieldTarget: this.hasHiddenFieldTarget,
      hasSelectedTarget: this.hasSelectedTarget,
      hasTriggerTarget: this.hasTriggerTarget,
      hasOptionsTarget: this.hasOptionsTarget
    })
    
    console.log('SearchableSelect connected - Values check:', {
      selected: this.selectedValue,
      text: this.selectedTextValue,
      placeholder: this.placeholderValue
    })
    
    // Test direct sur l'élément cliquable
    if (this.hasTriggerTarget) {
      console.log('🔍 Found trigger target:', this.triggerTarget)
      this.triggerTarget.addEventListener('click', (e) => {
        console.log('🖱️ Direct trigger click handler triggered!', e)
        e.preventDefault()
        e.stopPropagation()
        this.toggle(e)
      })
    }
  }

  disconnect() {
    document.removeEventListener('click', this.boundCloseOnClickOutside)
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  storeInitialOptions() {
    // Récupérer toutes les options depuis le HTML
    const optionElements = this.optionsTarget.querySelectorAll('[data-option-value]')
    this.allOptions = Array.from(optionElements).map(el => ({
      value: el.dataset.optionValue,
      text: el.dataset.optionText || el.textContent.trim(),
      html: el.innerHTML
    }))
    this.filteredOptions = [...this.allOptions]
  }

  // Ouvrir/fermer le dropdown
  toggle(event) {
    console.log('Toggle method called!', {
      event: event,
      isOpen: this.isOpen,
      hasDropdownTarget: this.hasDropdownTarget,
      dropdownElement: this.dropdownTarget
    })
    
    if (event) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    }
    
    if (this.isOpen) {
      this.close()
    } else {
      // Délai pour éviter que closeOnClickOutside se déclenche immédiatement
      setTimeout(() => {
        this.open()
      }, 10)
    }
  }

  open() {
    console.log('Open method called', {
      isOpen: this.isOpen,
      hasDropdownTarget: this.hasDropdownTarget,
      hasInputTarget: this.hasInputTarget,
      filteredOptions: this.filteredOptions.length
    })
    
    if (this.isOpen) return
    
    this.isOpen = true
    
    // Le dropdown est maintenant en position absolute, 
    // pas besoin de calculs de position manuels
    
    // Retirer la classe hidden et ajouter les classes d'ouverture
    this.dropdownTarget.classList.remove('hidden')
    if (this.hasOpenClasses) {
      this.dropdownTarget.classList.add(...this.openClasses)
    }
    
    if (this.hasInputTarget) {
      this.inputTarget.focus()
    }
    
    // Afficher les premiers résultats
    this.renderOptions(this.filteredOptions.slice(0, this.maxResultsValue))
    
    // Animation d'ouverture
    requestAnimationFrame(() => {
      this.dropdownTarget.style.transform = 'translateY(0)'
      this.dropdownTarget.style.opacity = '1'
    })
  }

  close() {
    console.log('Close method called', { isOpen: this.isOpen })
    
    if (!this.isOpen) return
    
    this.isOpen = false
    this.highlightedIndex = -1
    
    if (this.hasOpenClasses) {
      this.dropdownTarget.classList.remove(...this.openClasses)
    }
    
    // Animation de fermeture
    this.dropdownTarget.style.transform = 'translateY(-10px)'
    this.dropdownTarget.style.opacity = '0'
    
    // Cacher après l'animation
    setTimeout(() => {
      this.dropdownTarget.classList.add('hidden')
    }, 200)
    
    // Effacer la recherche
    if (this.hasInputTarget) {
      this.inputTarget.value = ''
    }
  }

  // Recherche au fur et à mesure de la frappe
  search(event) {
    const query = event.target.value.trim().toLowerCase()
    
    // Effacer le timeout précédent
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    // Débounce la recherche
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 150)
    
    // Ouvrir le dropdown si pas encore ouvert
    if (!this.isOpen) {
      this.open()
    }
  }

  async performSearch(query) {
    if (query.length === 0) {
      // Afficher les premiers résultats par défaut
      this.filteredOptions = [...this.allOptions]
      this.renderOptions(this.filteredOptions.slice(0, this.maxResultsValue))
      return
    }
    
    if (query.length < this.minCharsValue) {
      this.showNoResults("Tapez au moins " + this.minCharsValue + " caractères")
      return
    }
    
    // Si on a une URL pour la recherche AJAX
    if (this.urlValue) {
      await this.searchRemote(query)
    } else {
      // Recherche locale
      this.searchLocal(query)
    }
  }

  searchLocal(query) {
    this.filteredOptions = this.allOptions.filter(option => 
      option.text.toLowerCase().includes(query)
    )
    
    if (this.filteredOptions.length === 0) {
      this.showNoResults("Aucun résultat pour \"" + query + "\"")
    } else {
      this.renderOptions(this.filteredOptions.slice(0, this.maxResultsValue))
    }
    
    this.highlightedIndex = -1
  }

  async searchRemote(query) {
    try {
      // Construire l'URL correctement
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('q', query)
      
      console.log('Fetching URL:', url.toString())
      
      const response = await fetch(url.toString())
      const data = await response.json()
      
      this.filteredOptions = data.results || []
      
      if (this.filteredOptions.length === 0) {
        this.showNoResults("Aucun résultat pour \"" + query + "\"")
      } else {
        this.renderOptions(this.filteredOptions.slice(0, this.maxResultsValue))
      }
      
      this.highlightedIndex = -1
    } catch (error) {
      console.error('Erreur lors de la recherche:', error)
      this.showNoResults("Erreur lors de la recherche")
    }
  }

  renderOptions(options) {
    const optionsHtml = options.map((option, index) => `
      <div class="searchable-select-option px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center transition-colors duration-150"
           data-action="click->searchable-select#selectOption"
           data-option-value="${option.value}"
           data-option-text="${option.text}"
           data-option-index="${index}">
        ${option.html || option.text}
      </div>
    `).join('')
    
    this.optionsTarget.innerHTML = optionsHtml
    this.noResultsTarget.classList.add('hidden')
  }

  showNoResults(message) {
    this.optionsTarget.innerHTML = ''
    this.noResultsTarget.textContent = message
    this.noResultsTarget.classList.remove('hidden')
  }

  // Sélectionner une option
  selectOption(event) {
    const optionElement = event.currentTarget
    const value = optionElement.dataset.optionValue
    const text = optionElement.dataset.optionText
    
    // Mettre à jour le champ caché
    this.hiddenFieldTarget.value = value
    this.selectedValue = value
    this.selectedTextValue = text
    
    // Afficher la sélection
    this.displaySelectedValue()
    
    // Fermer le dropdown
    this.close()
    
    // Déclencher un événement pour notifier le changement
    this.hiddenFieldTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  displaySelectedValue() {
    if (this.hasSelectedTarget && this.selectedTextValue) {
      this.selectedTarget.textContent = this.selectedTextValue
      this.selectedTarget.classList.remove('hidden')
    }
  }

  // Effacer la sélection
  clear() {
    this.hiddenFieldTarget.value = ''
    this.selectedValue = ''
    this.selectedTextValue = ''
    
    if (this.hasSelectedTarget) {
      this.selectedTarget.classList.add('hidden')
    }
    
    this.hiddenFieldTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // Navigation clavier
  handleKeydown(event) {
    if (!this.isOpen) {
      if (event.key === 'Enter' || event.key === 'ArrowDown') {
        event.preventDefault()
        this.open()
      }
      return
    }
    
    const visibleOptions = this.optionsTarget.querySelectorAll('.searchable-select-option')
    
    switch (event.key) {
      case 'Escape':
        event.preventDefault()
        this.close()
        break
        
      case 'ArrowDown':
        event.preventDefault()
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, visibleOptions.length - 1)
        this.updateHighlight()
        break
        
      case 'ArrowUp':
        event.preventDefault()
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1)
        this.updateHighlight()
        break
        
      case 'Enter':
        event.preventDefault()
        if (this.highlightedIndex >= 0 && visibleOptions[this.highlightedIndex]) {
          visibleOptions[this.highlightedIndex].click()
        }
        break
    }
  }

  updateHighlight() {
    const options = this.optionsTarget.querySelectorAll('.searchable-select-option')
    
    options.forEach((option, index) => {
      if (index === this.highlightedIndex) {
        option.classList.add(...this.highlightClasses, 'bg-blue-100', 'text-blue-900')
      } else {
        option.classList.remove(...this.highlightClasses, 'bg-blue-100', 'text-blue-900')
      }
    })
  }

  closeOnClickOutside(event) {
    console.log('closeOnClickOutside called', {
      target: event.target,
      isInsideElement: this.element.contains(event.target),
      isOpen: this.isOpen
    })
    
    if (!this.element.contains(event.target) && this.isOpen) {
      console.log('Closing dropdown from outside click')
      this.close()
    }
  }
}